import type { ReactNode } from "react";
import { Box } from "../../components/ui/box";
import { Text } from "../../components/ui/text";
import { useChartColors, type ChartColorToken } from "./colors";
import { useHeatmapColorScale, useHeatmapOpacityScale } from "./heatmap";
import type { Omittable } from "../../lib/props";

/**
 * Legend components (verbatim `@visx/legend`) plus the two house legends:
 * {@link ChartLegend} for categorical series and {@link ChartRampLegend} for
 * sequential ramps.
 */
export {
  Legend,
  LegendLinear,
  LegendQuantile,
  LegendThreshold,
  LegendSize,
  LegendItem,
  LegendLabel,
} from "@visx/legend";

export type ChartLegendItem = Omittable<{
  /** The series/slice display label. */
  label: string;
  /** The color this item's mark wears — the same assignment slot the chart
   * paints (`useCategoricalColors().tokens[i]` / `.token(label)`, or an
   * owned token). Missing = the ramp's head, the rule a mark applies. */
  colorToken?: ChartColorToken;
  /** Optional aligned value, such as a count or a count with share. */
  value?: ReactNode;
}>;

export type ChartLegendProps = Omittable<{
  /** Items in display order — one entry per series/slice, straight off the
   * chart's ONE color assignment. */
  items: readonly ChartLegendItem[];
  /** `row` wraps whole items onto new lines; `column` stacks them. */
  direction?: "row" | "column";
  /** Main-axis placement, for a legend narrower than its box. */
  justify?: "start" | "center" | "end";
}>;

/**
 * THE categorical legend every series chart pairs with its marks, built from
 * the library's own primitives so the type, gaps and swatch shape sit on the
 * system scale. House rules:
 *  - each label stays on ONE line — "Residential Roof" never breaks into
 *    "Residential" / "Roof" — while a `row` legend wraps whole items onto a
 *    new line when it runs out of width;
 *  - swatches are 10px `rounded-sm` squares beside `xs` muted labels, so a
 *    narrow card's legend doesn't crowd the chart;
 *  - optional values use tabular numerals and align at the far edge of a
 *    column legend, turning composition legends into a compact data table;
 *  - color arrives as the chart's own tokens (`useCategoricalColors`), so
 *    swatches and marks can never disagree.
 */
export function ChartLegend({ items, direction = "row", justify }: ChartLegendProps) {
  const c = useChartColors();
  if (items.length === 0) return null;
  const row = direction === "row";
  return (
    <Box
      direction={direction}
      wrap={row ? true : undefined}
      align={row ? "center" : undefined}
      justify={justify}
      gap={row ? "sm" : "xs"}
    >
      {items.map((item) => (
        <Box
          key={item.label}
          direction="row"
          align="center"
          gap="xs"
          minWidth={0}
          fill={row ? undefined : "width"}
        >
          {/* The token resolves to a live `var()` string; `backgroundColor`
              evaluates it and re-resolves on a theme flip (like SVG fill), so
              nothing is measured in JS. */}
          <Box
            rounded="sm"
            width={10}
            height={10}
            shrink={false}
            style={{ backgroundColor: c.paint(item.colorToken) }}
          />
          <Box minWidth={0} flex={row ? undefined : 1}>
            <Text size="xs" variant="muted" truncate>
              {item.label}
            </Text>
          </Box>
          {item.value != null ? (
            <Text size="xs" variant="muted" tabular>
              {item.value}
            </Text>
          ) : null}
        </Box>
      ))}
    </Box>
  );
}

export type ChartRampLegendProps = Omittable<{
  /** Largest value represented by the ramp. A non-positive range renders nothing. */
  max: number;
  /** Semantic hue shared with the heatmap marks. */
  colorToken?: ChartColorToken;
  /** Distribution shape shared with the heatmap color scale. */
  shape?: "linear" | "sqrt" | "constant";
  /** Visual channel used to encode magnitude. */
  encoding?: "color" | "opacity" | "both";
  /** Fixed system density for the ramp. */
  steps?: 3 | 5;
  startLabel?: string;
  endLabel?: string;
}>;

/**
 * Compact sequential ramp legend for custom chart primitives. It builds the
 * same semantic single-hue color and opacity scales as the heatmap, so a
 * consumer cannot pass unrelated CSS colors or hand-tuned swatch geometry.
 */
export function ChartRampLegend({
  max,
  colorToken = "primary",
  shape = "linear",
  encoding = "color",
  steps = 5,
  startLabel,
  endLabel,
}: ChartRampLegendProps) {
  const colorScale = useHeatmapColorScale(max, {
    shape: encoding === "opacity" ? "constant" : shape,
    colorToken,
  });
  const opacityScale = useHeatmapOpacityScale(max);
  if (max <= 0) return null;

  const swatches = Array.from({ length: steps }, (_, index) => {
    const ratio = index / (steps - 1);
    const value = Math.max(1, max * ratio);
    return {
      color: colorScale(value),
      opacity: encoding === "color" ? 1 : opacityScale(value),
    };
  });

  return (
    <Box direction="row" align="center" justify="end" gap="2xs">
      {startLabel ? (
        <Text size="xs" variant="muted" truncate>
          {startLabel}
        </Text>
      ) : null}
      <Box direction="row" align="center" gap="2xs">
        {swatches.map((swatch, index) => (
          <Box
            key={index}
            data-slot="chart-ramp-swatch"
            rounded="sm"
            width={10}
            height={10}
            style={{ backgroundColor: swatch.color, opacity: swatch.opacity }}
          />
        ))}
      </Box>
      {endLabel ? (
        <Text size="xs" variant="muted" truncate>
          {endLabel}
        </Text>
      ) : null}
    </Box>
  );
}
